import React from 'react';
import TutorialManager, { Step } from '../../TutorialManager';
import { TUTORIAL_OCF } from '../../TutorialsContainer';
import styles from './OCFTutorial.module.scss';
import PIEText from '@src/shared/components/PIEText';
import { PIEComponentUtil } from '@src/shared/components/PIEComponentUtil';
import PIEButton from '@src/shared/components/PIEButton';
import { IconChevronLeft, IconClose } from '@justeattakeaway/pie-icons-react';
import useDeviceDetect from '@src/shared/hooks/use-device-detect';
import { useCommonPersistentStore } from '@src/shared/store/common-persistent-store';
import { useTranslation } from 'react-i18next';
import PIEDivider from '@src/shared/components/PIEDivider';

const TOTAL_PAGE_COUNT_FOR_TUTORIAL = 6;

interface componentProps {
  onTutorialEnd?: () => void;
  onTutorialStart?: () => void;
  startFromStep?: number;
}

const OCFTutorial = ({ onTutorialEnd, onTutorialStart, startFromStep }: componentProps) => {
  const { isResolutionMobile } = useDeviceDetect();
  const { setOCFTutorialIsActive } = useCommonPersistentStore();
  const { t } = useTranslation();
  const onBeforeStartHandler = () => {
    setOCFTutorialIsActive(true);
    onTutorialStart?.();
  };
  const onAfterEndHandler = () => {
    setOCFTutorialIsActive(false);
    onTutorialEnd?.();
  };

  const getSelector = () => {
    const alternateHeaderUserIconSelectorQuery = `[data-qa="mobile-header-show-user-menu-button"]`;
    const alternateHeaderUserIcon = document.querySelector(alternateHeaderUserIconSelectorQuery);
    if (alternateHeaderUserIcon) {
      return alternateHeaderUserIconSelectorQuery;
    } else {
      return '#user-logo';
    }
  };
  const renderPagination = (
    currentPage: number,
    totalPage: number,
    nextStep: () => void,
    previousStep = () => {}
  ) => {
    return (
      <div className={styles.paginationContainer}>
        <div className={styles.pageIndicator}>
          {currentPage > 1 && <IconChevronLeft size="xs" onClick={previousStep} />}{' '}
          {t('ocf_screens.OCF_TUTORIAL_STEP')} {currentPage} of {totalPage}
        </div>
        <PIEButton
          mode="primary"
          text={
            currentPage === totalPage
              ? t('ocf_screens.OCF_TUTORIAL_FINISH')
              : t('ocf_screens.OCF_TUTORIAL_NEXT')
          }
          onClick={() => {
            nextStep();
          }}
          size={'small'}
          type={'button'}
        />
      </div>
    );
  };

  const renderStep1 = (nextStep: () => void, goStep: (newStep: number) => void) => {
    return (
      <PIEComponentUtil paddingLeft="var(--xds-spacing-b)">
        <PIEComponentUtil marginBottom="var(--xds-spacing-e)" marginTop="var(--xds-spacing-b)">
          <div className={styles.headerContainer}>
            <PIEText
              size="var(--xds-font-size-20)"
              color="var(--xds-color-content-light)"
              fontWeight="var(--xds-font-weight-extrabold)"
              text={t('ocf_screens.OCF_TUTORIAL_HEADER_CONNECT')}
            />
            <IconClose size="m" onClick={() => goStep(7)} />
          </div>
        </PIEComponentUtil>
        <PIEComponentUtil marginBottom="var(--xds-spacing-d)">
          <PIEText
            truncate={false}
            size="var(--xds-font-size-16)"
            color="var(--xds-color-content-light)"
            fontWeight="var(--xds-font-weight-regular)"
            text={t('ocf_screens.OCF_TUTORIAL_STEP1')}
          />
        </PIEComponentUtil>
        {renderPagination(1, TOTAL_PAGE_COUNT_FOR_TUTORIAL, nextStep)}
      </PIEComponentUtil>
    );
  };
  const renderStep2 = (
    nextStep: () => void,
    goStep: (newStep: number) => void,
    previousStep = () => {}
  ) => {
    return (
      <PIEComponentUtil paddingLeft="var(--xds-spacing-b)">
        <PIEComponentUtil marginBottom="var(--xds-spacing-e)" marginTop="var(--xds-spacing-b)">
          <div className={styles.headerContainer}>
            <PIEText
              size="var(--xds-font-size-20)"
              color="var(--xds-color-content-light)"
              fontWeight="var(--xds-font-weight-extrabold)"
              text={t('ocf_screens.OCF_TUTORIAL_HEADER_HOW_CONNECT')}
            />
            <IconClose size="m" onClick={() => goStep(7)} />
          </div>
        </PIEComponentUtil>
        <PIEComponentUtil marginBottom="var(--xds-spacing-d)">
          <PIEText
            truncate={false}
            size="var(--xds-font-size-16)"
            color="var(--xds-color-content-light)"
            fontWeight="var(--xds-font-weight-regular)"
            text={t('ocf_screens.OCF_TUTORIAL_STEP2')}
          />
        </PIEComponentUtil>
        {renderPagination(2, TOTAL_PAGE_COUNT_FOR_TUTORIAL, nextStep, previousStep)}
      </PIEComponentUtil>
    );
  };
  const renderStep3 = (
    nextStep: () => void,
    goStep: (newStep: number) => void,
    previousStep = () => {}
  ) => {
    return (
      <PIEComponentUtil paddingLeft="var(--xds-spacing-b)">
        <PIEComponentUtil marginBottom="var(--xds-spacing-e)" marginTop="var(--xds-spacing-b)">
          <div className={styles.headerContainer}>
            <PIEText
              size="var(--xds-font-size-20)"
              color="var(--xds-color-content-light)"
              fontWeight="var(--xds-font-weight-extrabold)"
              text={t('ocf_screens.OCF_TUTORIAL_HEADER_HOW_CONNECT')}
            />
            <IconClose size="m" onClick={() => goStep(7)} />
          </div>
        </PIEComponentUtil>
        <PIEComponentUtil marginBottom="var(--xds-spacing-d)">
          <PIEText
            truncate={false}
            size="var(--xds-font-size-16)"
            color="var(--xds-color-content-light)"
            fontWeight="var(--xds-font-weight-regular)"
            text={t('ocf_screens.OCF_TUTORIAL_STEP3')}
          />
        </PIEComponentUtil>
        {renderPagination(3, TOTAL_PAGE_COUNT_FOR_TUTORIAL, nextStep, previousStep)}
      </PIEComponentUtil>
    );
  };
  const renderStep4 = (
    nextStep: () => void,
    goStep: (newStep: number) => void,
    previousStep = () => {}
  ) => {
    return (
      <PIEComponentUtil paddingLeft="var(--xds-spacing-b)">
        <PIEComponentUtil marginBottom="var(--xds-spacing-e)" marginTop="var(--xds-spacing-b)">
          <div className={styles.headerContainer}>
            <PIEText
              size="var(--xds-font-size-20)"
              color="var(--xds-color-content-light)"
              fontWeight="var(--xds-font-weight-extrabold)"
              text={t('ocf_screens.OCF_TUTORIAL_HEADER_HOW_CONNECT')}
            />
            <IconClose size="m" onClick={() => goStep(7)} />
          </div>
        </PIEComponentUtil>
        <PIEComponentUtil marginBottom="var(--xds-spacing-d)">
          <PIEText
            truncate={false}
            size="var(--xds-font-size-16)"
            color="var(--xds-color-content-light)"
            fontWeight="var(--xds-font-weight-regular)"
            text={t('ofc_screens.OCF_TUTORIAL_STEP4')}
          />
        </PIEComponentUtil>
        {renderPagination(4, TOTAL_PAGE_COUNT_FOR_TUTORIAL, nextStep, previousStep)}
      </PIEComponentUtil>
    );
  };
  const renderStep5 = (
    nextStep: () => void,
    goStep: (newStep: number) => void,
    previousStep = () => {}
  ) => {
    return (
      <PIEComponentUtil paddingLeft="var(--xds-spacing-b)">
        <PIEComponentUtil marginBottom="var(--xds-spacing-e)" marginTop="var(--xds-spacing-b)">
          <div className={styles.headerContainer}>
            <PIEText
              size="var(--xds-font-size-20)"
              color="var(--xds-color-content-light)"
              fontWeight="var(--xds-font-weight-extrabold)"
              text={t('ocf_screens.OCF_TUTORIAL_HEADER_MANAGE_SETTINGS')}
            />
            <IconClose size="m" onClick={() => goStep(7)} />
          </div>
        </PIEComponentUtil>
        <PIEComponentUtil marginBottom="var(--xds-spacing-d)">
          <PIEText
            truncate={false}
            size="var(--xds-font-size-16)"
            color="var(--xds-color-content-light)"
            fontWeight="var(--xds-font-weight-regular)"
            text={t('ocf_screens.OCF_TUTORIAL_STEP5')}
          />
        </PIEComponentUtil>
        {renderPagination(5, TOTAL_PAGE_COUNT_FOR_TUTORIAL, nextStep, previousStep)}
      </PIEComponentUtil>
    );
  };
  const renderStep6 = (
    nextStep: () => void,
    goStep: (newStep: number) => void,
    previousStep = () => {}
  ) => {
    return (
      <PIEComponentUtil paddingLeft="var(--xds-spacing-b)">
        <PIEComponentUtil marginBottom="var(--xds-spacing-e)" marginTop="var(--xds-spacing-b)">
          <div className={styles.headerContainer}>
            <PIEText
              size="var(--xds-font-size-20)"
              color="var(--xds-color-content-light)"
              fontWeight="var(--xds-font-weight-extrabold)"
              text={t('ocf_screens.OCF_TUTORIAL_HEADER_MANAGE_CONNECTION')}
            />
            <IconClose size="m" onClick={() => goStep(7)} />
          </div>
        </PIEComponentUtil>
        <PIEComponentUtil marginBottom="var(--xds-spacing-d)">
          <PIEText
            truncate={false}
            size="var(--xds-font-size-16)"
            color="var(--xds-color-content-light)"
            fontWeight="var(--xds-font-weight-regular)"
            text={t('ocf_screens.OCF_TUTORIAL_STEP6')}
          />
        </PIEComponentUtil>
        {renderPagination(6, TOTAL_PAGE_COUNT_FOR_TUTORIAL, nextStep, previousStep)}
      </PIEComponentUtil>
    );
  };

  const renderStep7 = (closeTutorial: () => void) => {
    const handleClose = () => {
      document.head.removeChild(document.getElementById('temp-tutorial-style')!);
      closeTutorial();
    };

    return (
      <div className={styles.reminderWrapper}>
        <div className={styles.headerContainer} style={{ alignItems: 'flex-start' }}>
          <PIEText
            truncate={false}
            size="var(--xds-font-size-13)"
            fontWeight="var(--xds-font-weight-regular)"
            text={t('ocf_screens.OCF_TUTORIAL_REVIEW_REMINDER')}
            color="#000"
          />
          <IconClose
            size="s"
            onClick={handleClose}
            style={{
              marginLeft: 'var(--xds-spacing-c)',
              color: 'var(--xds-color-content-disabled)',
            }}
          />
        </div>
        <PIEDivider />
        <div onClick={handleClose}>
          <div style={{ cursor: 'pointer' }}>
            <PIEText
              truncate={false}
              size="var(--xds-font-size-13)"
              color="#000"
              fontWeight="var(--xds-font-weight-bold)"
              text={t('ocf_screens.OCF_TUTORIAL_REVIEW_OK')}
            />
          </div>
        </div>
      </div>
    );
  };

  const stepsCompatibleWithDesktop: Array<Step> = [
    {
      targetElementSelector: `[data-qa="courier-page-add-courier-button"] button`,
      content: ({ nextStep, goStep }) => renderStep1(nextStep, goStep),
      tooltipSettings: () => ({
        modClickAway: false,
        position: 'bottom',
        align: 'start',
        panelWidth: 460,
        modFluid: true,
      }),
    },
    {
      targetElementSelector: '#qr-code-container',
      content: ({ nextStep, goStep, previousStep }) => renderStep2(nextStep, goStep, previousStep),
      beforeStepLeave({ isMovingToPreviousPage }) {
        if (isMovingToPreviousPage) {
          const closeElement = document.querySelector(
            `[data-qa="modal-header-action-close"]`
          ) as HTMLElement;
          if (closeElement) {
            closeElement?.click();
          }
        }
      },
      beforeStepInitialized: () => {
        (
          document.querySelector(
            `[data-qa="courier-page-add-courier-button"] button`
          ) as HTMLElement
        )?.click();
        setTimeout(() => {
          (document.querySelector('#show-qr-code-selection') as HTMLElement)?.click();
          setTimeout(() => {
            const popupOverlayElement = document.querySelector(
              '.ReactModal__Overlay'
            ) as HTMLElement;
            if (popupOverlayElement) {
              popupOverlayElement.style.zIndex = '9';
            }
          }, 30);
        }, 30);
      },
      delayBeforeStepInitialize: 300,
      tooltipSettings: () => ({
        modClickAway: false,
        position: 'bottom',
        align: 'start',
        panelWidth: 460,
        modFluid: true,
      }),
    },
    {
      content: ({ nextStep, goStep, previousStep }) => renderStep3(nextStep, goStep, previousStep),
      targetElementSelector: '#qr-connection-code-container',
      tooltipSettings: () => ({
        modClickAway: false,
        position: 'top',
        align: 'start',
        panelWidth: 460,
        modFluid: true,
      }),
    },
    {
      content: ({ nextStep, goStep, previousStep }) => renderStep4(nextStep, goStep, previousStep),
      targetElementSelector: '#qr-code-share-button button',
      beforeStepInitialized: () => {
        setTimeout(() => {
          const popupOverlayElement = document.querySelector('.ReactModal__Overlay') as HTMLElement;
          if (popupOverlayElement) {
            popupOverlayElement.style.zIndex = '8';
          }
        }, 350);
      },
      delayBeforeStepInitialize: 500,
      tooltipSettings: () => ({
        modClickAway: false,
        position: 'top',
        align: 'start',
        panelWidth: 460,
        modFluid: true,
      }),
    },
    {
      content: ({ nextStep, goStep, previousStep }) => renderStep5(nextStep, goStep, previousStep),
      beforeStepLeave({ isMovingToPreviousPage }) {
        if (isMovingToPreviousPage) {
          const closeElement = document.querySelector(`#courierDetailViewHeader`) as HTMLElement;
          if (closeElement) {
            closeElement?.click();
          }
          setTimeout(() => {
            (
              document.querySelector(
                `[data-qa="courier-page-add-courier-button"] button`
              ) as HTMLElement
            )?.click();
            setTimeout(() => {
              (document.querySelector('#show-qr-code-selection') as HTMLElement)?.click();
            }, 100);
          }, 100);
        }
      },
      beforeStepInitialized: () => {
        (document.querySelectorAll(`[data-qa="card-element"]`)[0] as HTMLElement).click();
        const closeElement = document.querySelector(
          `[data-qa="modal-header-action-close"]`
        ) as HTMLElement;
        if (closeElement) {
          closeElement?.click();
        }
        setTimeout(() => {
          const popupOverlayElement = document.querySelector('.ReactModal__Overlay') as HTMLElement;
          if (popupOverlayElement) {
            popupOverlayElement.style.zIndex = '9';
          }
        }, 100);
      },
      delayBeforeStepInitialize: 500,
      targetElementSelector: '#courier-order-toggle',
      tooltipSettings: () => ({
        modClickAway: false,
        position: 'top',
        align: 'start',
        panelWidth: 450,
        modFluid: true,
      }),
    },
    {
      content: ({ nextStep, goStep, previousStep }) => renderStep6(nextStep, goStep, previousStep),
      targetElementSelector: '#courier-data-action-buttons',
      tooltipSettings: () => ({
        modClickAway: false,
        position: 'top',
        align: 'start',
        panelWidth: 450,
        modFluid: true,
      }),
    },
    {
      content: ({ closeTutorial }) => renderStep7(closeTutorial),
      targetElementSelector: getSelector(),
      useTooltipStepSpecific: false,
      beforeStepInitialized: () => {
        const closeElement = document.querySelector(
          `[data-qa="modal-header-action-close"]`
        ) as HTMLElement;
        if (closeElement) {
          closeElement?.click();
        }
        (document.querySelector(`#courierDetailViewHeader`) as HTMLElement)?.click();
        setTimeout(() => {
          const style = document.createElement('style');
          style.id = 'temp-tutorial-style';
          style.textContent = `
        #step_content { margin-left: -210px; }
    `;
          document.head.appendChild(style);
        }, 100);
      },
    },
  ];

  const stepsCompatibleWithMobile: Array<Step> = [
    {
      targetElementSelector: `[data-qa="courier-page-add-courier-button"]`,
      content: ({ nextStep, goStep }) => renderStep1(nextStep, goStep),
      tooltipSettings: () => ({
        modClickAway: false,
        position: 'bottom',

        align: 'start',
        panelWidth: screen.width - 32,
        modFluid: true,
      }),
    },
    {
      targetElementSelector: '#qr-code-container',
      content: ({ nextStep, goStep, previousStep }) => renderStep2(nextStep, goStep, previousStep),
      beforeStepInitialized: () => {
        (
          document.querySelector('[data-qa="courier-page-add-courier-button"]') as HTMLElement
        )?.click();
        setTimeout(() => {
          (document.querySelector('#show-qr-code-selection') as HTMLElement)?.click();
        }, 300);
      },
      beforeStepLeave({ isMovingToPreviousPage }) {
        if (isMovingToPreviousPage) {
          (
            document.querySelector(`#delivery-dashboard-bottom-sheet-close-button`) as HTMLElement
          )?.click();
        }
      },
      delayBeforeStepInitialize: 1000,
      tooltipSettings: () => ({
        modClickAway: false,
        position: 'bottom',
        align: 'start',
        panelWidth: screen.width - 32,
        modFluid: true,
      }),
    },
    {
      content: ({ nextStep, goStep, previousStep }) => renderStep3(nextStep, goStep, previousStep),
      targetElementSelector: '#qr-connection-code-container',
      tooltipSettings: () => ({
        modClickAway: false,
        position: 'top',
        align: 'center',
        panelWidth: screen.width - 32,
        modFluid: true,
      }),
    },
    {
      content: ({ nextStep, goStep, previousStep }) => renderStep4(nextStep, goStep, previousStep),
      targetElementSelector: '#qr-code-share-button button',
      beforeStepLeave({ isMovingToNextPage }) {
        if (isMovingToNextPage) {
          (
            document.querySelector(`#delivery-dashboard-bottom-sheet-close-button`) as HTMLElement
          )?.click();
        }
      },
      beforeStepInitialized: () => {
        const qrButton = document.querySelector('#qr-code-share-button button');
        if (!qrButton) {
          (
            document.querySelector('[data-qa="courier-page-add-courier-button"]') as HTMLElement
          )?.click();
          setTimeout(() => {
            (document.querySelector('#show-qr-code-selection') as HTMLElement)?.click();
          }, 350);
        }
      },
      delayBeforeStepInitialize: 500,
      tooltipSettings: ({ closeTutorial }) => ({
        modClickAway: false,
        position: 'top',
        align: 'center',
        panelWidth: screen.width - 32,
        modFluid: true,
        onClose: () => {
          closeTutorial();
        },
      }),
    },
    {
      content: ({ nextStep, goStep, previousStep }) => renderStep5(nextStep, goStep, previousStep),
      beforeStepLeave({ isMovingToPreviousPage }) {
        if (isMovingToPreviousPage) {
          (document.querySelector(`#courierDetailViewHeader`) as HTMLElement)?.click();
          setTimeout(() => {
            (
              document.querySelector('[data-qa="courier-page-add-courier-button"]') as HTMLElement
            )?.click();
            setTimeout(() => {
              (document.querySelector('#show-qr-code-selection') as HTMLElement)?.click();
            }, 300);
          }, 100);
        }
      },

      beforeStepInitialized: () => {
        (document.querySelectorAll(`[data-qa="card-element"]`)[0] as HTMLElement).click();
      },
      delayBeforeStepInitialize: 800,
      targetElementSelector: '#courier-order-toggle',
      tooltipSettings: () => ({
        modClickAway: false,
        position: 'top',
        align: 'center',
        panelWidth: screen.width - 32,
        modFluid: true,
      }),
    },
    {
      content: ({ nextStep, goStep, previousStep }) => renderStep6(nextStep, goStep, previousStep),
      targetElementSelector: '#courier-data-action-buttons',
      tooltipSettings: () => ({
        modClickAway: false,
        position: 'top',
        align: 'center',
        panelWidth: screen.width - 32,
        modFluid: true,
      }),
    },
    {
      content: ({ closeTutorial }) => renderStep7(closeTutorial),
      targetElementSelector: getSelector(),
      useTooltipStepSpecific: false,
      beforeStepInitialized: () => {
        (document.querySelector(`#courierDetailViewHeader`) as HTMLElement)?.click();
        (
          document.querySelector(`#delivery-dashboard-bottom-sheet-close-button`) as HTMLElement
        )?.click();

        setTimeout(() => {
          const style = document.createElement('style');
          style.id = 'temp-tutorial-style';
          style.textContent = `
        #step_content { margin-left: -210px; }
    `;
          document.head.appendChild(style);
        }, 100);
      },
    },
  ];

  return (
    <TutorialManager
      onBeforeStart={onBeforeStartHandler}
      onAfterEnd={onAfterEndHandler}
      steps={isResolutionMobile ? stepsCompatibleWithMobile : stepsCompatibleWithDesktop}
      useTooltip={true}
      tutorialID={TUTORIAL_OCF}
      predefinedStep={startFromStep}
    />
  );
};
export default OCFTutorial;
